import {
  ClipboardDocumentCheckIcon,
  CursorArrowRaysIcon,
  LinkIcon,
  MapPinIcon,
  SparklesIcon,
  TicketIcon,
} from "@heroicons/react/20/solid";
import {OtsIconActionButton} from "../components/OtsButton";
import {
  OtsDataTable,
  OtsDataTableBody,
  OtsDataTableCell,
  OtsDataTableHead,
  OtsDataTableHeaderCell,
  OtsDataTableRow,
} from "../components/OtsDataTable";
import {OtsDateTime} from "../components/OtsDateTime";
import {OtsProgressBar} from "../components/OtsProgressBar";
import {OtsTooltip} from "../components/OtsTooltip";
import {OtsTruncatedText} from "../components/OtsTruncatedText";
import {OtsTray} from "../components/OtsTray";
import {getOtsStatusChipClassName} from "../navigation/OtsStatusStrip";
import type {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {
  type ActiveShiftRow,
  elapsedShiftTime,
  isShiftLocationStale,
  shiftLocationAge,
  shiftLocationLabel,
  shiftDestinationDetail,
  shiftContentsDisplay,
} from "./active-shifts";

export type ActiveShiftsTrayProps = {
  rows: readonly ActiveShiftRow[];
  now: number;
  regionName?: string;
  loading?: boolean;
  error?: string;
  locationsLoading?: boolean;
  locationsError?: string;
  onOpenChange?: (open: boolean) => void;
  onViewMap?: (row: ActiveShiftRow) => void;
  onViewDispatch?: (row: ActiveShiftRow) => void;
  onViewTickets?: (row: ActiveShiftRow) => void;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

const destinationSources = {
  MANUAL: {label: "Manual", Icon: CursorArrowRaysIcon},
  DISPATCH: {label: "Dispatch", Icon: ClipboardDocumentCheckIcon},
  ASSIGNMENT: {label: "Assignment", Icon: LinkIcon},
  INFERRED: {label: "Inferred", Icon: SparklesIcon},
};

/** Shift records own the count; locations only enrich the compact, automatically refreshed rows. */
export function ActiveShiftsTray({
  rows,
  now,
  regionName,
  loading,
  error,
  locationsLoading,
  locationsError,
  onOpenChange,
  onViewMap,
  onViewDispatch,
  onViewTickets,
  brand,
  colorScheme,
}: ActiveShiftsTrayProps) {
  const label = loading
    ? "Active shifts loading"
    : error
    ? "Active shifts unavailable"
    : `${rows.length} Active Shift${rows.length === 1 ? "" : "s"}`;
  return (
    <OtsTray
      label="Active shifts"
      showHeader={false}
      maxHeight="min(20rem,65dvh)"
      trigger={
        <>
          <span
            aria-hidden="true"
            className="size-1.5 shrink-0 rounded-full bg-[var(--ots-success)] motion-safe:animate-pulse"
          />
          <span>{label}</span>
        </>
      }
      triggerLabel={label}
      triggerClassName={getOtsStatusChipClassName(error ? "warning" : "default")}
      placement="bottom start"
      size="md"
      brand={brand}
      colorScheme={colorScheme}
      onOpenChange={onOpenChange}
    >
      {({close}) => (
        <>
          {(error || locationsError) && (
            <p
              role="alert"
              className="border-b border-[var(--ots-outline-variant)] px-2 py-1.5 text-[11px] text-[var(--ots-warning)]"
            >
              {[error, locationsError].filter(Boolean).join(" ")}
            </p>
          )}
          {locationsLoading && !loading && (
            <p role="status" className="px-2 py-1 text-[11px] text-[var(--ots-text-muted)]">
              Loading locations…
            </p>
          )}
          {loading ? (
            <p role="status" className="px-2 py-3 text-xs text-[var(--ots-text-muted)]">
              Loading active shifts…
            </p>
          ) : rows.length === 0 ? (
            <p className="px-2 py-3 text-xs text-[var(--ots-text-muted)]">
              {error ? "Shift count is unavailable." : "No active shifts in this region."}
            </p>
          ) : (
            <OtsDataTable
              aria-label="Active shifts"
              density="compact"
              contained={false}
              tableClassName="w-full table-fixed !min-w-[30rem] !text-[11px] leading-tight [&_td]:!px-2 [&_td]:!py-1 [&_th]:!px-2 [&_th]:!py-1"
            >
              {regionName && <caption className="sr-only">Active shifts · {regionName}</caption>}
              <colgroup>
                <col className="w-1/4" />
                <col className="w-[28%]" />
                <col />
                <col className="w-[6.25rem]" />
              </colgroup>
              <OtsDataTableHead className="!text-[10px] !tracking-normal">
                <OtsDataTableRow>
                  {["Driver / started", "Vehicle / trailer", "Location / activity", "Actions"].map((heading) => (
                    <OtsDataTableHeaderCell key={heading}>
                      <OtsTruncatedText>{heading}</OtsTruncatedText>
                    </OtsDataTableHeaderCell>
                  ))}
                </OtsDataTableRow>
              </OtsDataTableHead>
              <OtsDataTableBody>
                {rows.map((row) => {
                  const category = row.contents?.category ?? row.freightCategory;
                  const materialType = row.contents?.materialType ?? row.freightType;
                  const estimateLabel =
                    category && !["WATER", "OIL", "MIXED"].includes(category) ? "Estimated contents" : "Estimated load";
                  const stale = isShiftLocationStale(row, now);
                  const load = shiftContentsDisplay(row, now);
                  const destinationDetail = shiftDestinationDetail(row, now);
                  const eta = !stale && row.activity !== "OFFLINE" && row.destination?.destination.etaDttm;
                  const currentEta = typeof eta === "number" && eta >= now ? eta : undefined;
                  const destinationSource = row.destination
                    ? destinationSources[row.destination.source as keyof typeof destinationSources] ?? {
                        label: row.destination.source,
                        Icon: MapPinIcon,
                      }
                    : undefined;
                  const DestinationIcon = destinationSource?.Icon;
                  const locationDetail = (
                    <>
                      {shiftLocationAge(row, now)}
                      {stale && row.position && !row.locationBeforeShift ? " · Stale" : ""}
                    </>
                  );
                  const destinationTooltip =
                    destinationDetail && row.destination ? (
                      <div className="space-y-1">
                        <div>{destinationDetail}</div>
                        {currentEta != null && (
                          <div>
                            ETA <OtsDateTime value={currentEta} format="time" />
                          </div>
                        )}
                        <div>Source: {row.destination.source}</div>
                        <div>{row.destination.reason}</div>
                        {row.possibleDestinations && (
                          <div>
                            <div className="font-semibold">Available candidates</div>
                            <ul className="list-inside list-disc">
                              {Object.entries(row.possibleDestinations).map(([source, candidate]) => (
                                <li key={source}>
                                  {candidate.siteName} · {source}
                                  {candidate.status === "ARRIVED" ? " · Arrived" : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : undefined;
                  return (
                    <OtsDataTableRow key={row.id}>
                      <OtsDataTableCell>
                        <OtsTruncatedText className="font-semibold">{row.driver}</OtsTruncatedText>
                        <OtsTruncatedText className="text-[10px] text-[var(--ots-text-muted)]">
                          <OtsDateTime value={row.startDttm} />
                          {" · "}
                          <span>{elapsedShiftTime(row.startDttm, now)}</span>
                        </OtsTruncatedText>
                      </OtsDataTableCell>
                      <OtsDataTableCell>
                        <OtsTruncatedText>
                          {row.vehicle || "No vehicle"}
                          <span className="text-[var(--ots-text-muted)]"> / {row.trailer || "No trailer"}</span>
                        </OtsTruncatedText>
                        <OtsTooltip
                          className="w-full min-w-0"
                          density="compact"
                          content={
                            <div className="text-[11px]">
                              <div>
                                {load.label}
                                {load.percent != null ? ` · ${load.percent}%` : ""}
                              </div>
                              {(category || materialType) && (
                                <div>{[category, materialType].filter(Boolean).join(" · ")}</div>
                              )}
                              <div>Contents based on recorded activity.</div>
                              {load.nearCapacity && <div>Near capacity</div>}
                              {load.overCapacity && <div>Over capacity · {load.percent}%</div>}
                              {row.contents?.sourceDttm != null && (
                                <div>
                                  Record updated: <OtsDateTime value={row.contents.sourceDttm} />
                                </div>
                              )}
                              {row.contents?.calculatedDttm != null && (
                                <div>
                                  Calculated: <OtsDateTime value={row.contents.calculatedDttm} />
                                </div>
                              )}
                            </div>
                          }
                        >
                          <div
                            tabIndex={0}
                            className="flex w-full min-w-0 items-center gap-1 whitespace-nowrap text-[10px] text-[var(--ots-text-muted)]"
                          >
                            {load.capacity != null && load.quantity != null && (
                              <OtsProgressBar
                                label={`${row.contents?.estimated === false ? "Contents" : estimateLabel} for ${
                                  row.driver
                                }`}
                                value={load.quantity}
                                max={load.capacity}
                                size="compact"
                                tone={load.tone ?? "neutral"}
                                valueText={`${load.label} · ${load.percent}%${
                                  load.overCapacity ? " · Over capacity" : load.nearCapacity ? " · Near capacity" : ""
                                }`}
                                className="!w-10 shrink-0"
                              />
                            )}
                            <span
                              className={`truncate ${
                                load.overCapacity
                                  ? "text-[var(--ots-error)]"
                                  : load.nearCapacity
                                  ? row.freightCategory === "WATER"
                                    ? "text-[var(--ots-load-water-near-capacity)]"
                                    : row.freightCategory === "OIL"
                                    ? "text-[var(--ots-text-muted)]"
                                    : "text-[var(--ots-warning)]"
                                  : ""
                              }`}
                            >
                              {load.label}
                              {load.overCapacity && <span className="sr-only"> · Over capacity</span>}
                            </span>
                          </div>
                        </OtsTooltip>
                      </OtsDataTableCell>
                      <OtsDataTableCell>
                        <OtsTruncatedText>{shiftLocationLabel(row, now)}</OtsTruncatedText>
                        <div className="flex min-w-0 items-center gap-1">
                          <OtsTruncatedText
                            className={`text-[10px] ${
                              stale ? "text-[var(--ots-warning)]" : "text-[var(--ots-text-muted)]"
                            }`}
                          >
                            {locationDetail}
                          </OtsTruncatedText>
                          {destinationTooltip && destinationSource && DestinationIcon && (
                            <OtsTooltip
                              content={destinationTooltip}
                              density="compact"
                              className="shrink-0"
                              panelClassName="whitespace-normal break-words"
                            >
                              <span
                                role="img"
                                aria-label={`${destinationSource.label} destination recommendation for ${row.driver}`}
                                tabIndex={0}
                                className="inline-flex size-3.5 items-center justify-center rounded-sm text-[var(--ots-text-muted)] focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--ots-primary)]"
                              >
                                <DestinationIcon aria-hidden="true" className="size-3" />
                              </span>
                            </OtsTooltip>
                          )}
                        </div>
                      </OtsDataTableCell>
                      <OtsDataTableCell>
                        <div className="flex items-center">
                          {onViewMap && (
                            <OtsIconActionButton
                              icon={<MapPinIcon />}
                              label={`View ${row.driver} on map`}
                              variant="text"
                              size="mini"
                              disabled={!row.position}
                              onClick={() => {
                                close();
                                onViewMap(row);
                              }}
                            />
                          )}
                          {onViewDispatch && (
                            <OtsIconActionButton
                              icon={<ClipboardDocumentCheckIcon />}
                              label={`View dispatches for ${row.driver}`}
                              variant="text"
                              size="mini"
                              onClick={() => {
                                close();
                                onViewDispatch(row);
                              }}
                            />
                          )}
                          {onViewTickets && (
                            <OtsIconActionButton
                              icon={<TicketIcon />}
                              label={`View tickets for ${row.driver}`}
                              variant="text"
                              size="mini"
                              onClick={() => {
                                close();
                                onViewTickets(row);
                              }}
                            />
                          )}
                        </div>
                      </OtsDataTableCell>
                    </OtsDataTableRow>
                  );
                })}
              </OtsDataTableBody>
            </OtsDataTable>
          )}
        </>
      )}
    </OtsTray>
  );
}
